import { Button } from "@/components/ui/button.tsx";
import { cn } from "@/lib/utils.ts";
import type {
  InstantBookColumn,
  InstantBookDayResponse,
  InstantBookEvent,
} from "@/types/instantBook.ts";
import { addDays, dayBoundsIn, todayIn } from "@/shared/dayBounds.ts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";

/**
 * The Instant Book picker: one day, one column per person, with the meeting
 * placed on the grid and draggable.
 *
 * Purpose-built rather than FullCalendar — its per-resource column view is a
 * paid tier, and at one day the grid is small enough to own outright.
 */

/** Exported for tests, which place by pixel exactly as a pointer does. */
export const PX_PER_HOUR = 44;
const MS_PER_HOUR = 60 * 60 * 1000;
const SNAP_MS = 30 * 60 * 1000;
/** The whole day is rendered; this is how much of it you see at once. */
const VIEWPORT_HOURS = 14;
/** Where the viewport opens, so the working day is what you land on. */
const DEFAULT_TOP_HOUR = 7;
/** Time-gutter width plus the column gap — the meeting starts where columns do. */
const GUTTER_PX = 52;
const HEADER_PX = 24;
const ALL_DAY_ROW_PX = 16;

export type PlacedSlot = { startMsSinceEpoch: number; endMsSinceEpoch: number };

export const InstantBookDayGrid = ({
  date,
  timezone,
  durationMinutes,
  day,
  columns,
  calendarsLoading,
  placedSlot,
  editable,
  onDateChange,
  onPlace,
}: {
  /** `YYYY-MM-DD` in `timezone`. Known to the caller without a request, which
   *  is what lets paging draw before any data arrives. */
  date: string;
  timezone: string;
  durationMinutes: number;
  /** This day's calendars, once they land. */
  day: InstantBookDayResponse | undefined;
  /** Who has a column. Carried across days — the people don't change — so a
   *  new day draws its columns immediately and fills in their events. */
  columns: InstantBookColumn[];
  calendarsLoading: boolean;
  /** Where the meeting sits on THIS day, or null. Owned by the caller so an
   *  in-flight placement can lead the server. */
  placedSlot: PlacedSlot | null;
  editable: boolean;
  onDateChange: (date: string) => void;
  /** Fires once per gesture, on release — never mid-drag. */
  onPlace: (slot: PlacedSlot, bookOverConflict: boolean) => void;
}) => {
  const { startMsSinceEpoch: dayStartMs, endMsSinceEpoch: dayEndMs } = useMemo(
    () => dayBoundsIn(date, timezone),
    [date, timezone],
  );

  // The placement being dragged, and the day it is being dragged on. Held
  // locally so a socket-driven refresh mid-gesture can't yank the meeting out
  // from under the pointer. The day is carried because a slot is only meaningful
  // on the day it was placed — drawn against another day's midnight it lands
  // off the grid entirely.
  const [draft, setDraft] = useState<{ date: string; slot: PlacedSlot } | null>(null);
  const placed = (draft?.date === date ? draft.slot : null) ?? placedSlot;

  // The whole day is rendered and scrolled, rather than a computed window —
  // otherwise the hours outside that window are simply unreachable.
  const height = ((dayEndMs - dayStartMs) / MS_PER_HOUR) * PX_PER_HOUR;
  const msToPx = useCallback(
    (ms: number) => ((ms - dayStartMs) / MS_PER_HOUR) * PX_PER_HOUR,
    [dayStartMs],
  );

  const allDayRows = useMemo(
    () => Math.max(0, ...columns.map((c) => c.events.filter((e) => e.allDay).length)),
    [columns],
  );
  const allDayPx = allDayRows > 0 ? allDayRows * ALL_DAY_ROW_PX + 4 : 0;

  // Open on the working day, or on the meeting when it sits outside it — a
  // 6am booking must not render off-screen.
  const scrollRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: positions once per rendered day
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const target = placed
      ? msToPx(placed.startMsSinceEpoch) - PX_PER_HOUR
      : DEFAULT_TOP_HOUR * PX_PER_HOUR;
    el.scrollTop = Math.max(0, Math.min(target, height - el.clientHeight));
  }, [date]);

  const commit = useCallback(
    (slot: PlacedSlot) => {
      onPlace(slot, overlapsAnyCommitment(columns, slot));
      // The caller shows the placement in the same tick, so the draft has done
      // its job on release. Holding it any longer outlived the gesture: the
      // meeting then redrew on every day the user paged to afterwards.
      setDraft(null);
    },
    [columns, onPlace],
  );

  const gestureRef = useRef<number | null>(null);
  // A gesture ends in a pointerup, and the browser follows that with a click on
  // the canvas below. `preventDefault` on pointerdown suppresses the compat
  // mouse events but never the click, so every drag and resize placed the
  // meeting twice: once where the user put it, then again wherever the pointer
  // happened to be sitting. Cleared on the next press, so a gesture that ends
  // without a click can't swallow a real tap.
  const gestureJustEndedRef = useRef(false);

  const startGesture = useCallback(
    (mode: "move" | "resize", event: React.PointerEvent) => {
      if (!editable || !placed) return;
      // One gesture at a time: a second pointerdown would otherwise start a
      // rival handler pair and commit twice on a single release.
      if (gestureRef.current !== null) return;
      event.preventDefault();
      event.stopPropagation();
      const originY = event.clientY;
      const origin = placed;
      const pointerId = event.pointerId;
      gestureRef.current = pointerId;
      const target = event.currentTarget as HTMLElement;
      target.setPointerCapture(pointerId);

      let latest = origin;
      const onMove = (e: PointerEvent) => {
        // A second pointer (multi-touch, stylus + finger) would otherwise drive
        // this handler with the first gesture's origin.
        if (e.pointerId !== pointerId) return;
        const deltaMs = snap(pxToMs(e.clientY - originY));
        const moved = applyGesture(mode, origin, deltaMs);
        // A resize clamps its end alone. Running it through clampToDay slid the
        // start up to keep the whole meeting in the day — moving the edge the
        // user isn't holding.
        latest =
          mode === "resize"
            ? clampResizeToDay(moved, dayEndMs)
            : clampToDay(moved, dayStartMs, dayEndMs);
        setDraft({ date, slot: latest });
      };
      const finish = (commitIt: boolean) => (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return;
        target.removeEventListener("pointermove", onMove);
        target.removeEventListener("pointerup", onUp);
        target.removeEventListener("pointercancel", onCancel);
        gestureRef.current = null;
        gestureJustEndedRef.current = true;
        // A plain click never moved anything — committing it would stamp the
        // time as user-placed and have the assistant narrate a move that never
        // happened. A cancelled gesture reverts for the same reason.
        if (commitIt && !isSameSlot(latest, origin)) {
          commit(latest);
        } else {
          setDraft(null);
        }
      };
      const onUp = finish(true);
      const onCancel = finish(false);
      target.addEventListener("pointermove", onMove);
      target.addEventListener("pointerup", onUp);
      target.addEventListener("pointercancel", onCancel);
    },
    [editable, placed, commit, date, dayStartMs, dayEndMs],
  );

  const canvasRef = useRef<HTMLDivElement>(null);

  // A tap puts the meeting where you tapped — on its own day or any other.
  // Dragging the meeting stops propagation, so this only ever sees a tap on
  // open grid.
  const placeAtTap = useCallback(
    (event: React.MouseEvent) => {
      if (!editable) return;
      if (gestureJustEndedRef.current) {
        gestureJustEndedRef.current = false;
        return;
      }
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const durationMs = placed
        ? placed.endMsSinceEpoch - placed.startMsSinceEpoch
        : durationMinutes * 60 * 1000;
      const startMsSinceEpoch = dayStartMs + snapDown(pxToMs(event.clientY - rect.top));
      const next = clampToDay(
        { startMsSinceEpoch, endMsSinceEpoch: startMsSinceEpoch + durationMs },
        dayStartMs,
        dayEndMs,
      );
      commit(next);
    },
    [editable, placed, durationMinutes, commit, dayStartMs, dayEndMs],
  );

  const hourTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let t = dayStartMs; t < dayEndMs; t += MS_PER_HOUR) ticks.push(t);
    return ticks;
  }, [dayStartMs, dayEndMs]);

  // Paging is unbounded forward — the meeting can go on any future day — and
  // stops at today, because a meeting in the past is not a thing to offer.
  const availableByDate = useMemo(
    () => new Map((day?.dayAvailability ?? []).map((d) => [d.date, d.hasAvailability])),
    [day?.dayAvailability],
  );
  const today = useMemo(() => todayIn(timezone), [timezone]);
  const prevDate = addDays(date, -1);
  const nextDate = addDays(date, 1);

  return (
    <div className="flex flex-col gap-2">
      {/* Three columns rather than space-between, so the day stays centred on
          the grid when the Previous button isn't there to balance Next. */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center">
        {/* Hidden rather than disabled at the past boundary — a greyed-out
            control invites a click that will never do anything. */}
        <div className="justify-self-start">
          {prevDate >= today ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDateChange(prevDate)}
              data-statsig-name="instant-book-prev-day"
            >
              <ChevronLeft className="size-4" />
              <DayLabel date={prevDate} hasAvailability={availableByDate.get(prevDate)} />
            </Button>
          ) : null}
        </div>
        <span className="px-2 text-sm font-semibold">{formatDayHeading(timezone, dayStartMs)}</span>
        <Button
          variant="ghost"
          size="sm"
          className="justify-self-end"
          onClick={() => onDateChange(nextDate)}
          data-statsig-name="instant-book-next-day"
        >
          <DayLabel date={nextDate} hasAvailability={availableByDate.get(nextDate)} />
          <ChevronRight className="size-4" />
        </Button>
      </div>

      {/* Headers sit outside the scroller so a name never scrolls away from
          the column it labels. */}
      <div className="flex gap-1 overflow-hidden">
        <div className="w-12 shrink-0" style={{ height: HEADER_PX + allDayPx }} />
        {columns.map((column) => (
          <div key={column.key} className="flex min-w-24 flex-1 flex-col">
            <div
              className={cn(
                "truncate text-center text-xs font-medium",
                column.self ? "text-foreground" : "text-muted-foreground",
              )}
              style={{ height: HEADER_PX }}
              title={columnTooltip(column)}
            >
              {column.name}
            </div>
            <div style={{ height: allDayPx }} className="flex flex-col gap-px">
              {column.events
                .filter((e) => e.allDay)
                .map((event, index) => (
                  <EventBlock key={eventKey(event, index)} event={event} variant="allDay" />
                ))}
            </div>
          </div>
        ))}
      </div>

      <div
        ref={scrollRef}
        className="overflow-y-auto"
        style={{ height: VIEWPORT_HOURS * PX_PER_HOUR }}
      >
        <div className="relative flex gap-1">
          <div className="relative w-12 shrink-0" style={{ height }}>
            {hourTicks.map((t, index) => (
              <span
                key={t}
                className={cn(
                  "absolute right-1 text-[10px] text-muted-foreground",
                  // Every label straddles its own line, except the first: there
                  // is nothing above midnight to straddle, so half of it fell
                  // outside the scroller and was clipped. That one hangs below
                  // its line instead.
                  index > 0 && "-translate-y-1/2",
                )}
                style={{ top: msToPx(t) }}
              >
                {formatHour(t, timezone)}
              </span>
            ))}
          </div>

          {columns.map((column) => (
            <PersonColumn
              key={column.key}
              column={column}
              height={height}
              hourTicks={hourTicks}
              loading={calendarsLoading}
              msToPx={msToPx}
            />
          ))}

          {/* The meeting spans every column — that side-by-side reading is the
              whole point of the view — so it's drawn over them all rather than
              inside one. Scrolls with the columns because it's their sibling. */}
          <div
            ref={canvasRef}
            className={cn("absolute inset-y-0 right-0", editable && "cursor-pointer")}
            style={{ left: GUTTER_PX }}
            onClick={placeAtTap}
            onPointerDownCapture={() => {
              gestureJustEndedRef.current = false;
            }}
            data-statsig-name="instant-book-place-slot"
          >
            {placed ? (
              <div
                className={cn(
                  "absolute inset-x-0 overflow-hidden rounded-md bg-primary/85 px-2 py-1 text-xs font-medium text-primary-foreground shadow-sm",
                  editable && "cursor-grab touch-none active:cursor-grabbing",
                )}
                style={{
                  top: msToPx(placed.startMsSinceEpoch),
                  height: Math.max(
                    16,
                    msToPx(placed.endMsSinceEpoch) - msToPx(placed.startMsSinceEpoch),
                  ),
                }}
                onPointerDown={editable ? (e) => startGesture("move", e) : undefined}
                data-statsig-name="instant-book-move-slot"
              >
                {formatRange(placed, timezone)}
                {editable ? (
                  // No fill of its own — a second colour across the bottom
                  // reads as the meeting having two halves. The cursor is the
                  // affordance.
                  <div
                    className="absolute inset-x-0 bottom-0 h-2 cursor-ns-resize touch-none"
                    onPointerDown={(e) => startGesture("resize", e)}
                    data-statsig-name="instant-book-resize-slot"
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

/** One person's day: their commitments against the hour grid. */
const PersonColumn = ({
  column,
  height,
  hourTicks,
  loading,
  msToPx,
}: {
  column: InstantBookColumn;
  height: number;
  hourTicks: number[];
  /** Their events haven't arrived. An empty column and an unloaded one must
   *  not look alike — "nobody is busy" is a claim. */
  loading: boolean;
  msToPx: (ms: number) => number;
}) => (
  <div className="flex min-w-24 flex-1 flex-col">
    <div className="relative border-l border-border" style={{ height }}>
      {hourTicks.map((t) => (
        <div
          key={t}
          className="absolute inset-x-0 border-t border-border/60"
          style={{ top: msToPx(t) }}
        />
      ))}
      {loading ? <div className="absolute inset-0 animate-pulse bg-muted/40" /> : null}
      {layoutOverlaps(column.events.filter((e) => !e.allDay)).map(
        ({ event, lane, lanes }, index) => (
          <div
            key={eventKey(event, index)}
            className="absolute"
            style={{
              top: msToPx(event.startMsSinceEpoch),
              height: Math.max(12, msToPx(event.endMsSinceEpoch) - msToPx(event.startMsSinceEpoch)),
              left: `${(lane / lanes) * 100}%`,
              width: `${100 / lanes}%`,
            }}
          >
            <EventBlock event={event} variant="timed" />
          </div>
        ),
      )}
    </div>
  </div>
);

/**
 * A commitment. A missing title renders as "Busy" — either the whole calendar
 * is free/busy-only or this one event is private, and in both cases claiming
 * to know more than we do would be the wrong call.
 */
const EventBlock = ({
  event,
  variant,
}: {
  event: InstantBookEvent;
  variant: "timed" | "allDay";
}) => (
  <div
    className={cn(
      "truncate px-1 text-[10px] leading-4",
      variant === "allDay"
        ? "rounded-sm bg-accent text-accent-foreground"
        : "h-full rounded-sm bg-muted py-0.5 leading-tight text-muted-foreground ring-1 ring-border",
    )}
    title={event.title ?? "Busy"}
  >
    {event.title ?? "Busy"}
  </div>
);

const DayLabel = ({ date, hasAvailability }: { date: string; hasAvailability?: boolean }) => (
  <span className={cn("text-xs", hasAvailability === false && "text-muted-foreground")}>
    {formatShortDay(date)}
  </span>
);

const columnTooltip = (column: InstantBookColumn) => {
  if (column.detail === "busyOnly") return `${column.name} shares free/busy only`;
  if (column.detail === "unavailable") return `${column.name}'s calendar couldn't be read`;
  return column.name;
};

/** Position included: two untitled all-day blocks clip to identical bounds. */
const eventKey = (event: InstantBookEvent, index: number) =>
  `${event.startMsSinceEpoch}-${event.endMsSinceEpoch}-${event.title ?? ""}-${index}`;

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

const pxToMs = (px: number) => (px / PX_PER_HOUR) * MS_PER_HOUR;
/** Exported for tests — the drag maths is the part worth pinning. */
export const snap = (ms: number) => Math.round(ms / SNAP_MS) * SNAP_MS;

/**
 * The slot a point falls inside, for placing by tap. Rounding to the nearest
 * boundary instead put half of every slot on the one below it — a tap at 9:15
 * booked 9:30. A drag still rounds, because there the number being snapped is
 * how far the pointer moved, not where it landed.
 */
export const snapDown = (ms: number) => Math.floor(ms / SNAP_MS) * SNAP_MS;

export const isSameSlot = (a: PlacedSlot, b: PlacedSlot) =>
  a.startMsSinceEpoch === b.startMsSinceEpoch && a.endMsSinceEpoch === b.endMsSinceEpoch;

/**
 * Whether the meeting sits on top of a commitment somebody actually has —
 * tested against the blocks drawn on the grid, which is exactly what the user
 * is looking at when they place it there.
 *
 * Deliberately NOT "outside the free ranges": those are already narrowed by the
 * user's meeting-type hours and by travel buffers, so a 7pm slot with every
 * calendar empty falls outside them. Calling that a conflict would be wrong
 * twice over, because the answer also tells approval it may skip its freshness
 * check — and an empty 7pm is precisely a slot still worth re-checking.
 */
export function overlapsAnyCommitment(columns: InstantBookColumn[], slot: PlacedSlot): boolean {
  return columns.some((column) =>
    column.events.some(
      (event) =>
        !event.allDay &&
        event.startMsSinceEpoch < slot.endMsSinceEpoch &&
        event.endMsSinceEpoch > slot.startMsSinceEpoch,
    ),
  );
}

/** Where a drag of `deltaMs` puts the meeting, before it's clamped to the day. */
export function applyGesture(
  mode: "move" | "resize",
  origin: PlacedSlot,
  deltaMs: number,
): PlacedSlot {
  if (mode === "move") {
    return {
      startMsSinceEpoch: origin.startMsSinceEpoch + deltaMs,
      endMsSinceEpoch: origin.endMsSinceEpoch + deltaMs,
    };
  }
  return {
    startMsSinceEpoch: origin.startMsSinceEpoch,
    // Never shorter than one snap step, or the meeting can be dragged out of
    // existence.
    endMsSinceEpoch: Math.max(origin.startMsSinceEpoch + SNAP_MS, origin.endMsSinceEpoch + deltaMs),
  };
}

/**
 * Keep the meeting inside the day it's being placed on.
 *
 * A meeting longer than the day is shortened rather than shunted: positioning a
 * too-long slot by its start alone puts it before the day begins.
 */
export function clampToDay(slot: PlacedSlot, dayStartMs: number, dayEndMs: number): PlacedSlot {
  const duration = Math.min(slot.endMsSinceEpoch - slot.startMsSinceEpoch, dayEndMs - dayStartMs);
  const start = Math.min(Math.max(slot.startMsSinceEpoch, dayStartMs), dayEndMs - duration);
  return { startMsSinceEpoch: start, endMsSinceEpoch: start + duration };
}

/**
 * Keep a resized meeting inside the day by pulling its end back, never its
 * start. The start is the edge the user is not holding, and a resize that moves
 * it changes a time they didn't touch.
 */
export function clampResizeToDay(slot: PlacedSlot, dayEndMs: number): PlacedSlot {
  return {
    startMsSinceEpoch: slot.startMsSinceEpoch,
    endMsSinceEpoch: Math.max(
      slot.startMsSinceEpoch + SNAP_MS,
      Math.min(slot.endMsSinceEpoch, dayEndMs),
    ),
  };
}

/**
 * Side-by-side lanes for one person's overlapping events, so a double-booked
 * hour shows both rather than hiding one behind the other.
 */
export function layoutOverlaps<T extends { startMsSinceEpoch: number; endMsSinceEpoch: number }>(
  events: T[],
): Array<{ event: T; lane: number; lanes: number }> {
  const sorted = [...events].sort((a, b) => a.startMsSinceEpoch - b.startMsSinceEpoch);
  const laneEnds: number[] = [];
  const placed = sorted.map((event) => {
    let lane = laneEnds.findIndex((end) => end <= event.startMsSinceEpoch);
    if (lane === -1) lane = laneEnds.length;
    laneEnds[lane] = event.endMsSinceEpoch;
    return { event, lane };
  });
  const lanes = Math.max(1, laneEnds.length);
  return placed.map((p) => ({ ...p, lanes }));
}

// ---------------------------------------------------------------------------
// Formatting — every label is drawn in the meeting's timezone, not the
// browser's, so a user reviewing a card from another zone reads the times the
// meeting will actually happen at.
// ---------------------------------------------------------------------------

const formatHour = (ms: number, timeZone: string) =>
  new Intl.DateTimeFormat("en-US", { hour: "numeric", timeZone }).format(new Date(ms));

/** The date string is already a local calendar date, so read it back as one. */
const formatShortDay = (date: string) =>
  new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone: "UTC" }).format(
    new Date(`${date}T12:00:00Z`),
  );

const formatDayHeading = (timeZone: string, dayStartMs: number) =>
  new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone,
  }).format(new Date(dayStartMs + 12 * MS_PER_HOUR));

const formatRange = (slot: PlacedSlot, timeZone: string) => {
  const fmt = new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", timeZone });
  return `${fmt.format(new Date(slot.startMsSinceEpoch))} – ${fmt.format(new Date(slot.endMsSinceEpoch))}`;
};
